import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import CartIcon from './CartIcon'
import UserMenu from './UserMenu'
import WishlistIcon from './WishlistIcon'
import { CATEGORY_GROUPS, SHOP_CATEGORIES, collectionPath, isGeneralGroup } from '../constants/shopCategories'
import { CONTACT_INFO, SITE_LINKS, SOCIAL_LINKS } from '../constants/siteLinks'
import { HOME_OFFERINGS } from '../constants/homeOfferings'
import { SERVICE_PAGES, servicePath, servicePathFor } from '../constants/servicePages'
import './HomeLanding.css'

const TOP_PERKS = [
  { icon: 'fa-truck-fast', text: 'Delivery across Rwanda' },
  { icon: 'fa-shield-halved', text: 'Authentic Products' },
  { icon: 'fa-headset', text: 'Local Support' },
]

const SOCIALS = SOCIAL_LINKS

// Services without shop products stay out of the Shop dropdown (they have their own menu items or popups)
const SHOP_MENU_EXCLUDE = ['software', 'paytv', 'training', 'networking']

/** Right-hand panel of the Shop dropdown, per category: photo, short description, linked shop category. */
const SHOP_MENU_DETAILS = {
  starlink: {
    image: '/assets/img/slider/starlink-daylight-4k.jpg',
    text: 'High-speed fiber and Starlink internet for homes, businesses and remote areas.',
    shopCategory: 'starlink',
  },
  cctv: {
    image: '/assets/img/service/security-cards/cctv-dual-web.jpg',
    text: 'Cameras, ready-to-install kits, recorders, alarms and intercoms to protect your site.',
    shopCategory: 'digital-security',
  },
  'access-control': {
    image: '/assets/img/service/security-cards/smart-lock-web.jpg',
    text: 'Door access, smart locks and intrusion alarms for offices, homes and institutions.',
  },
  networking: {
    image: '/assets/img/service/network-cards/structured-cabling.jpg',
    text: 'Switches, Wi-Fi and structured cabling designed and installed by our engineers.',
  },
  power: {
    image: '/assets/img/shop/categories/power-accessories.png',
    text: 'UPS systems and power backup that keep your equipment running.',
    shopCategory: 'power-backup',
  },
  shop: {
    image: '/assets/img/product/shop.jpeg',
    text: 'Browse every product in our store — genuine equipment, delivered across Rwanda.',
  },
}

/** Close an open popup on outside click or Escape. */
function useDismiss(open, ref, close) {
  useEffect(() => {
    if (!open) return undefined
    const onPointer = (e) => {
      if (!ref.current?.contains(e.target)) close()
    }
    const onKey = (e) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, ref, close])
}

/** Link to an offering: a page, or a service popup on the home page. */
export function OfferingLink({ item, onOpenService, className, children, onNavigate, tabIndex, ...rest }) {
  if (item.serviceId) {
    return (
      <button
        type="button"
        className={className}
        tabIndex={tabIndex}
        {...rest}
        onClick={() => {
          onNavigate?.()
          onOpenService?.(item.serviceId)
        }}
      >
        {children}
      </button>
    )
  }
  return (
    <Link to={item.to} className={className} onClick={onNavigate} tabIndex={tabIndex} {...rest}>
      {children}
    </Link>
  )
}

/** Slide-in mobile menu: the same entries as the desktop menu (Shop opens the same categories), plus contact details. */
function MobileMenu({ open, onClose, onOpenService }) {
  const path = useLocation().pathname
  const shopActive = path === SITE_LINKS.shop || path.startsWith('/collections') || path.startsWith('/product-details')
  const [shopOpen, setShopOpen] = useState(shopActive)
  const servicesActive = path.startsWith('/services/')
  const [servicesOpen, setServicesOpen] = useState(servicesActive)
  const shopItems = HOME_OFFERINGS.filter((item) => !SHOP_MENU_EXCLUDE.includes(item.id))

  useEffect(() => {
    if (!open) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previous
    }
  }, [open, onClose])

  const contacts = [
    { icon: 'fa-envelope', label: 'Email', value: CONTACT_INFO.email, href: CONTACT_INFO.emailHref },
    { icon: 'fa-phone', label: 'Phone', value: CONTACT_INFO.phone, href: CONTACT_INFO.phoneHref },
    { icon: 'fa-location-dot', label: 'Location', value: CONTACT_INFO.address, href: CONTACT_INFO.mapsHref, external: true },
  ]

  return (
    <>
      <div className="tv-offcanvas-area">
        <aside className={`itoffcanvas fab-offcanvas${open ? ' opened' : ''}`} aria-hidden={!open}>
          <div className="fab-offcanvas__top">
            <Link to={SITE_LINKS.home} className="fab-offcanvas__brand" onClick={onClose}>
              <img src="/assets/img/logo/F_logo.png" alt="Fabritech" />
            </Link>
            <button type="button" className="fab-offcanvas__close close-btn" aria-label="Close menu" onClick={onClose}>
              <i className="fa-solid fa-xmark" aria-hidden="true" />
            </button>
          </div>

          <div className="fab-offcanvas__body">
            <p className="fab-offcanvas__kicker">Menu</p>
            <nav className="tv-menu-mobile" aria-label="Mobile">
              <ul className="fab-offcanvas-nav">
                <li>
                  <Link to={SITE_LINKS.home} onClick={onClose} className={path === SITE_LINKS.home ? 'is-active' : undefined}>
                    <span>Home</span>
                    <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                  </Link>
                </li>
                <li className={`hm-mshop${shopOpen ? ' is-open' : ''}`}>
                  <button
                    type="button"
                    className={`hm-mnav${shopActive ? ' is-active' : ''}`}
                    aria-expanded={shopOpen}
                    aria-controls="hm-mshop-panel"
                    onClick={() => setShopOpen((value) => !value)}
                  >
                    <span>Shop</span>
                    <i className="fa-solid fa-chevron-down hm-mshop__caret" aria-hidden="true" />
                  </button>
                  {shopOpen && (
                    <ul className="hm-mshop__panel" id="hm-mshop-panel">
                      {shopItems.map((item) => {
                        const detail = SHOP_MENU_DETAILS[item.id] || {}
                        const subs = (CATEGORY_GROUPS.find((group) => group.id === detail.shopCategory)?.subcategories || []).filter(
                          (sub) => !isGeneralGroup(sub)
                        )
                        return (
                          <li key={item.id}>
                            <OfferingLink item={item} onOpenService={onOpenService} onNavigate={onClose} className="hm-mshop__item">
                              <span className="hm-mshop__icon" aria-hidden="true">
                                <i className={`fa-solid ${item.icon}`} />
                              </span>
                              <span>{item.name}</span>
                            </OfferingLink>
                            {subs.length > 0 && (
                              <ul className="hm-mshop__subs">
                                {subs.map((sub) => (
                                  <li key={sub.id}>
                                    <Link to={collectionPath(sub.id)} onClick={onClose}>
                                      {sub.name}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </li>
                <li className={`hm-mshop${servicesOpen ? ' is-open' : ''}`}>
                  <button
                    type="button"
                    className={`hm-mnav${servicesActive ? ' is-active' : ''}`}
                    aria-expanded={servicesOpen}
                    aria-controls="hm-mservices-panel"
                    onClick={() => setServicesOpen((value) => !value)}
                  >
                    <span>Services</span>
                    <i className="fa-solid fa-chevron-down hm-mshop__caret" aria-hidden="true" />
                  </button>
                  {servicesOpen && (
                    <ul className="hm-mshop__panel" id="hm-mservices-panel">
                      {SERVICE_PAGES.map((service) => (
                        <li key={service.slug}>
                          <Link to={servicePath(service.slug)} onClick={onClose} className="hm-mshop__item">
                            <span className="hm-mshop__icon" aria-hidden="true">
                              <i className={`fa-solid ${service.icon}`} />
                            </span>
                            <span>{service.menuLabel}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
                <li>
                  <a href={SITE_LINKS.contact} onClick={onClose}>
                    <span>Contact Us</span>
                    <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                  </a>
                </li>
              </ul>
            </nav>
          </div>

          <div className="fab-offcanvas__foot">
            <p className="fab-offcanvas__kicker">Contact</p>
            <ul className="fab-offcanvas-contact">
              {contacts.map((c) => (
                <li key={c.label}>
                  <a href={c.href} {...(c.external ? { target: '_blank', rel: 'noreferrer' } : {})}>
                    <span className="fab-offcanvas-contact__icon" aria-hidden="true">
                      <i className={`fa-solid ${c.icon}`} />
                    </span>
                    <span className="fab-offcanvas-contact__text">
                      <span className="fab-offcanvas-contact__label">{c.label}</span>
                      <span className="fab-offcanvas-contact__value">{c.value}</span>
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
      <div className={`body-overlay${open ? ' apply' : ''}`} onClick={onClose} aria-hidden={!open} />
    </>
  )
}

/**
 * Site header used on the home and shop pages: contact/perks bar, logo + product search + account
 * actions, a menu row (Home, Shop dropdown, Services dropdown, Contact Us), and the mobile menu.
 * Service links go to the service pages (/services/:slug); `onOpenService(id)` can override that.
 */
function MainHeader({ cartCount = 0, onCartClick, onOpenService }) {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [query, setQuery] = useState('')
  const [category, setCategory] = useState('')
  const [menuOpen, setMenuOpen] = useState(false)
  const closeMenu = useCallback(() => setMenuOpen(false), [])
  const openService = onOpenService || ((id) => navigate(servicePathFor(id)))
  const [shopOpen, setShopOpen] = useState(false)
  const [shopItemId, setShopItemId] = useState('starlink')
  const [servicesOpen, setServicesOpen] = useState(false)
  const servicesRef = useRef(null)
  const [catOpen, setCatOpen] = useState(false)
  const shopRef = useRef(null)
  const catRef = useRef(null)

  useDismiss(shopOpen, shopRef, () => setShopOpen(false))
  useDismiss(servicesOpen, servicesRef, () => setServicesOpen(false))
  useDismiss(catOpen, catRef, () => setCatOpen(false))


  // Keep the search box in step with the shop URL (/shop?q=…&category=…)
  const urlQuery = searchParams.get('q') || ''
  const urlCategory = searchParams.get('category') || ''

  // Close the shop dropdown whenever the page changes
  useEffect(() => {
    setShopOpen(false)
    setServicesOpen(false)
  }, [location.pathname, location.search])

  const path = location.pathname
  const shopActive = path === SITE_LINKS.shop || path.startsWith('/collections') || path.startsWith('/product-details')
  // The product search lives on shopping pages; elsewhere the menu takes its place in the main bar
  const showSearch = shopActive

  useEffect(() => {
    if (location.pathname !== SITE_LINKS.shop) return
    setQuery(urlQuery)
    setCategory(SHOP_CATEGORIES.some((cat) => cat.id === urlCategory) ? urlCategory : '')
  }, [location.pathname, urlQuery, urlCategory])

  const submitSearch = (e) => {
    e.preventDefault()
    const params = new URLSearchParams()
    const term = query.trim()
    if (term) params.set('q', term)
    if (category) params.set('category', category)
    const qs = params.toString()
    navigate(qs ? `${SITE_LINKS.shop}?${qs}` : SITE_LINKS.shop)
  }



  const menu = (
    <ul className="hm-menu">
      <li>
        <Link to={SITE_LINKS.home} className={`hm-menu__link${path === SITE_LINKS.home ? ' is-active' : ''}`}>
          Home
        </Link>
      </li>
      <li
        className={`hm-menu__shop${shopOpen ? ' is-open' : ''}`}
        ref={shopRef}
        onMouseEnter={() => setShopOpen(true)}
        onMouseLeave={() => setShopOpen(false)}
      >
        <button
          type="button"
          className={`hm-menu__link hm-menu__link--shop${shopActive ? ' is-active' : ''}`}
          aria-expanded={shopOpen}
          aria-haspopup="true"
          onClick={() => setShopOpen((open) => !open)}
        >
          Shop
          <i className="fa-solid fa-chevron-down hm-caret" aria-hidden="true" />
        </button>
        {shopOpen && (() => {
          const items = HOME_OFFERINGS.filter((item) => !SHOP_MENU_EXCLUDE.includes(item.id))
          const current = items.find((item) => item.id === shopItemId) || items[0]
          const detail = SHOP_MENU_DETAILS[current.id] || {}
          const subs = (CATEGORY_GROUPS.find((group) => group.id === detail.shopCategory)?.subcategories || []).filter((sub) => !isGeneralGroup(sub))
          return (
            <div className="hm-mega">
              <ul className="hm-mega__list">
                {items.map((item) => (
                  <li key={item.id} onMouseEnter={() => setShopItemId(item.id)} onFocus={() => setShopItemId(item.id)}>
                    <OfferingLink
                      item={item}
                      onOpenService={openService}
                      onNavigate={() => setShopOpen(false)}
                      className={`hm-mega__item${item.id === current.id ? ' is-active' : ''}`}
                    >
                      <i className={`fa-solid ${item.icon}`} aria-hidden="true" />
                      <span>{item.name}</span>
                      <i className="fa-solid fa-chevron-right hm-mega__arrow" aria-hidden="true" />
                    </OfferingLink>
                  </li>
                ))}
              </ul>
              <div className="hm-mega__detail" key={current.id}>
                <span className="hm-mega__detail-img" style={{ backgroundImage: `url('${detail.image || current.image}')` }} />
                <div className="hm-mega__detail-body">
                  <strong>{current.name}</strong>
                  <p>{detail.text}</p>
                  {subs.length > 0 && (
                    <ul className="hm-mega__subs">
                      {subs.map((sub) => (
                        <li key={sub.id}>
                          <Link to={collectionPath(sub.id)} onClick={() => setShopOpen(false)}>
                            {sub.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                  <OfferingLink item={current} onOpenService={openService} onNavigate={() => setShopOpen(false)} className="hm-mega__cta">
                    {current.id === 'shop' ? 'Browse Shop' : detail.shopCategory ? 'Shop Now' : 'Learn More'}
                    <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                  </OfferingLink>
                </div>
              </div>
            </div>
          )
        })()}
      </li>
      <li
        className={`hm-menu__shop hm-menu__services${servicesOpen ? ' is-open' : ''}`}
        ref={servicesRef}
        onMouseEnter={() => setServicesOpen(true)}
        onMouseLeave={() => setServicesOpen(false)}
      >
        <button
          type="button"
          className={`hm-menu__link${path.startsWith('/services/') ? ' is-active' : ''}`}
          aria-expanded={servicesOpen}
          aria-haspopup="true"
          onClick={() => setServicesOpen((open) => !open)}
        >
          Services
          <i className="fa-solid fa-chevron-down hm-caret" aria-hidden="true" />
        </button>
        {servicesOpen && (
          <div className="hm-svc">
            <ul className="hm-svc__grid">
              {SERVICE_PAGES.map((service) => (
                <li key={service.slug}>
                  <Link to={servicePath(service.slug)} className={`hm-svc__item${path === servicePath(service.slug) ? ' is-active' : ''}`}>
                    <span className="hm-svc__icon" aria-hidden="true">
                      <i className={`fa-solid ${service.icon}`} />
                    </span>
                    <span>
                      <strong>{service.menuLabel}</strong>
                      <small>{service.summary}</small>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
            <div className="hm-svc__foot">
              <span>
                <i className="fa-solid fa-headset" aria-hidden="true" />
                Not sure what you need? Our team will advise you.
              </span>
              <a href={SITE_LINKS.contact}>
                Talk to us
                <i className="fa-solid fa-arrow-right" aria-hidden="true" />
              </a>
            </div>
          </div>
        )}
      </li>
      <li>
        <a href={SITE_LINKS.contact} className="hm-menu__link">
          Contact Us
        </a>
      </li>
    </ul>
  )

  return (
    <>
    <header className="hm-header">
      <div className="hm-topbar">
        <div className="hm-container hm-topbar__inner">
          <div className="hm-topbar__group">
            <a href={CONTACT_INFO.phoneHref}>
              <i className="fa-solid fa-phone" aria-hidden="true" />
              {CONTACT_INFO.phone}
            </a>
            <a href={CONTACT_INFO.emailHref} className="hm-hide-md">
              <i className="fa-solid fa-envelope" aria-hidden="true" />
              {CONTACT_INFO.email}
            </a>
            <a href={CONTACT_INFO.mapsHref} target="_blank" rel="noreferrer" className="hm-hide-lg">
              <i className="fa-solid fa-location-dot" aria-hidden="true" />
              {CONTACT_INFO.address}, Kigali
            </a>
          </div>
          <div className="hm-topbar__group hm-topbar__perks">
            {TOP_PERKS.map((perk) => (
              <span key={perk.text}>
                <i className={`fa-solid ${perk.icon}`} aria-hidden="true" />
                {perk.text}
              </span>
            ))}
          </div>
          <div className="hm-topbar__group hm-topbar__social">
            {SOCIALS.map((social) => (
              <a key={social.label} href={social.href} target="_blank" rel="noreferrer" aria-label={social.label}>
                <i className={`fa-brands ${social.icon}`} aria-hidden="true" />
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="hm-mainbar">
        <div className={`hm-container hm-mainbar__inner${showSearch ? '' : ' is-centered'}`}>
          <Link to={SITE_LINKS.home} className="hm-logo" aria-label="Fabritech home">
            <img src="/assets/img/logo/F_logo.png" alt="Fabritech" />
          </Link>

          {showSearch ? (
            <form className="hm-search" role="search" onSubmit={submitSearch}>
              <i className="fa-solid fa-magnifying-glass hm-search__icon" aria-hidden="true" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search for Starlink, CCTV, networking, UPS…"
                aria-label="Search products"
              />
              {/* Custom picker: the template's main.js turns every native <select> into a jQuery widget React can't read. */}
              <div className="hm-search__cat" ref={catRef}>
                <button
                  type="button"
                  className="hm-search__cat-btn"
                  aria-haspopup="listbox"
                  aria-expanded={catOpen}
                  aria-label="Product category"
                  onClick={() => setCatOpen((open) => !open)}
                >
                  <span>{SHOP_CATEGORIES.find((cat) => cat.id === category)?.name || 'All Categories'}</span>
                  <i className="fa-solid fa-chevron-down hm-caret" aria-hidden="true" />
                </button>
                {catOpen && (
                  <ul className="hm-search__cat-menu" role="listbox" aria-label="Product category">
                    {[{ id: '', name: 'All Categories' }, ...SHOP_CATEGORIES].map((cat) => (
                      <li key={cat.id || 'all'}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={category === cat.id}
                          className={category === cat.id ? 'is-active' : undefined}
                          onClick={() => {
                            setCategory(cat.id)
                            setCatOpen(false)
                          }}
                        >
                          {cat.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button type="submit" aria-label="Search">
                <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
              </button>
            </form>
          ) : (
            <nav className="hm-inline-nav" aria-label="Main">
              {menu}
            </nav>
          )}

          <div className="hm-actions">
            <UserMenu label="Account" />
            <WishlistIcon label="Wishlist" />
            <CartIcon cartCount={cartCount} onClick={onCartClick} label="Cart" />
            <button
              type="button"
              className="hm-burger"
              aria-label="Open menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(true)}
            >
              <i className="fa-solid fa-bars" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      {showSearch && (
        <nav className="hm-navbar" aria-label="Main">
          <div className="hm-container hm-navbar__inner">
            {menu}
          </div>
        </nav>
      )}
    </header>
    <MobileMenu open={menuOpen} onClose={closeMenu} onOpenService={openService} />
    </>
  )
}

export default MainHeader
